/**
 * ATELIER LUXURY SHOPIFY THEME - PREDICTIVE SEARCH ENGINE
 */

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.modal = this.closest('.predictive-search-modal');
    this.input = this.querySelector('input[type="search"]');
    this.resultsContainer = this.querySelector('[data-predictive-search-results]');
    this.cachedResults = {};
    this.initEvents();
  }

  initEvents() {
    this.input?.addEventListener('input', debounce((e) => {
      this.onChange(e);
    }, 300));

    // Keyboard navigation
    this.addEventListener('keydown', this.onKeyDown.bind(this));

    // Modal triggers & closers
    document.querySelectorAll('[data-search-trigger]').forEach((trigger) => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });

    document.querySelectorAll('[data-search-close]').forEach((closer) => {
      closer.addEventListener('click', () => this.close());
    });

    this.modal?.querySelector('.predictive-search-modal__backdrop')?.addEventListener('click', () => this.close());
  }

  open() {
    this.modal?.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    setTimeout(() => this.input?.focus(), 100);
  }

  close() {
    this.modal?.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  async onChange() {
    const searchTerm = this.input.value.trim();

    if (!searchTerm.length) {
      this.clearResults();
      return;
    }

    if (this.cachedResults[searchTerm]) {
      this.renderResults(this.cachedResults[searchTerm], searchTerm);
      return;
    }

    try {
      const url = `${window.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&resources[type]=product,collection,page&resources[limit]=6`;
      const res = await fetch(url);
      const data = await res.json();

      if (res.ok) {
        this.cachedResults[searchTerm] = data.resources.results;
        this.renderResults(data.resources.results, searchTerm);
      }
    } catch (err) {
      console.error('Predictive search error:', err);
    }
  }

  renderResults(results, term) {
    if (!this.resultsContainer) return;

    const products = results.products || [];
    const collections = results.collections || [];

    if (!products.length && !collections.length) {
      this.resultsContainer.innerHTML = `
        <div class="predictive-search-empty text-center" style="padding: 3rem 0;">
          <p>No results found for "${term}".</p>
        </div>
      `;
      return;
    }

    let html = '<div class="predictive-search-results__grid">';

    // Products Column
    if (products.length) {
      html += `
        <div class="predictive-search-column">
          <h4 class="predictive-search-results__heading">Products</h4>
          <div class="predictive-search-list">
            ${products.map((p) => `
              <a href="${p.url}" class="predictive-search-item">
                <div class="predictive-search-item__media">
                  <img src="${p.featured_image.url || ''}" alt="${p.title}" width="55" height="70">
                </div>
                <div class="predictive-search-item__info">
                  <span class="predictive-search-item__title">${p.title}</span>
                  <span class="predictive-search-item__price">${window.formatMoney(p.price)}</span>
                </div>
              </a>
            `).join('')}
          </div>
        </div>
      `;
    }

    // Collections Column
    if (collections.length) {
      html += `
        <div class="predictive-search-column">
          <h4 class="predictive-search-results__heading">Collections</h4>
          <div class="predictive-search-list">
            ${collections.map((c) => `
              <a href="${c.url}" class="predictive-search-item">
                <div class="predictive-search-item__info">
                  <span class="predictive-search-item__title">${c.title}</span>
                </div>
              </a>
            `).join('')}
          </div>
        </div>
      `;
    }

    html += '</div>';

    // View All Link
    html += `
      <div class="predictive-search-view-all">
        <a href="/search?q=${encodeURIComponent(term)}" class="button button--secondary">
          View all results for "${term}" &rarr;
        </a>
      </div>
    `;

    this.resultsContainer.innerHTML = html;
  }

  clearResults() {
    if (this.resultsContainer) this.resultsContainer.innerHTML = '';
  }

  onKeyDown(e) {
    if (e.key === 'Escape') {
      this.close();
    }
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}
